/**
 * The little physical effects of the paper-theatre draw stage — shared by
 * HangingDraw (standard spreads) and HangingSpread (premium spreads).
 *
 * All imperative (Web Animations / spawned elements), never Vue state per frame,
 * and all no-ops under `prefers-reduced-motion`.
 */

export const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Decode images ahead of time (while the entrance plays): a preloaded image is still
 * decoded lazily on first paint — for a card face that's the moment it turns over, a
 * visible hitch. Fire-and-forget.
 */
export function predecode(urls: string[]) {
  if (typeof Image === 'undefined') return;
  for (const url of urls) {
    const img = new Image();
    img.src = url;
    img.decode?.().catch(() => {});
  }
}

/** How long a card takes to turn over on its strings — pass `flip-duration` = this to TarotCard. */
export const TURN_MS = 760;

/** `setTimeout` as a promise that collapses to 0ms under reduced motion. */
export const motionSleep = (ms: number) =>
  new Promise(resolve => setTimeout(resolve, prefersReducedMotion() ? 0 : ms));

/* ---------------- motion, sampled into keyframes ----------------
 * Anything on strings moves by curves sampled into dense keyframes played with linear
 * easing (never 4–5 hand-placed keyframes joined by straight lines — velocity kinks
 * read as choppy). Three rules keep it smooth:
 *  - every oscillation is *windowed*: it eases to exactly 0 (and zero velocity) by its
 *    last keyframe, so nothing jumps when the animation ends;
 *  - swings are *bounded* by design (a few degrees), not raw physics — a bar braking
 *    from full speed would fling a card on a short string tens of degrees;
 *  - swings on one element are *additive* (`composite: 'add'`), so a gust, a tap and
 *    the entrance blend instead of cutting each other off mid-swing. */

const SAMPLE_MS = 16;

/** Sample `fn(tMs)` → transform string into evenly spaced keyframes over `duration`. */
export function sampled(duration: number, fn: (t: number) => string): Keyframe[] {
  const n = Math.max(2, Math.round(duration / SAMPLE_MS));
  return Array.from({ length: n + 1 }, (_, i) => ({ transform: fn((i / n) * duration) }));
}

/** A damped oscillation that starts at 0 moving (impulse): peaks near 1, rings down. */
export function ring(t: number, periodMs: number, zeta: number) {
  const wd = (2 * Math.PI) / (periodMs / 1000);
  const w = wd / Math.sqrt(1 - zeta * zeta);
  const s = t / 1000;
  return Math.exp(-zeta * w * s) * Math.sin(wd * s);
}

/** 1 until `from` (a share of `duration`), then eases to exactly 0 at `duration` (smoothstep, zero slope at both ends). */
export function fadeOut(t: number, duration: number, from = 0.55) {
  const p = (t / duration - from) / (1 - from);
  if (p <= 0) return 1;
  if (p >= 1) return 0;
  return 1 - p * p * (3 - 2 * p);
}

/** A windowed ring: rings down and is exactly 0 at `duration`. */
export const settle = (t: number, duration: number, periodMs: number, zeta: number) =>
  ring(t, periodMs, zeta) * fadeOut(t, duration);

const easeOutCubic = (p: number) => 1 - (1 - p) ** 3;
const easeInQuad = (p: number) => p * p;
const clamp01 = (p: number) => (p < 0 ? 0 : p > 1 ? 1 : p);

/** Rotate `el` about its hang point by `deg(t)` over `duration` — additive, so swings blend. */
function rock(el: HTMLElement, duration: number, deg: (t: number) => number, delay = 0) {
  el.animate(sampled(duration, t => `rotate(${deg(t).toFixed(2)}deg)`), {
    duration,
    delay,
    easing: 'linear',
    composite: 'add',
  });
}

/** A card swinging on its strings after a tap (rotate about the hang point). */
export function swing(el: HTMLElement | null | undefined, strength = 1, delay = 0) {
  if (!el || prefersReducedMotion()) return;
  const duration = 1300;
  rock(el, duration, t => 5 * strength * settle(t, duration, 560, 0.2), delay);
}

/** A bow on the strings: the card tips its face towards the audience and dips. */
export function bow(el: HTMLElement | null | undefined, strength = 1, delay = 0) {
  if (!el || prefersReducedMotion()) return;
  el.animate(
    [
      { transform: 'none' },
      { transform: `perspective(700px) rotateX(${-34 * strength}deg) translateY(${2 * strength}cqw)`, offset: 0.42 },
      { transform: 'none' },
    ],
    { duration: BOW_MS, delay, easing: 'cubic-bezier(.45,0,.3,1)' },
  );
}
export const BOW_MS = 800;

/** Sweep a glint of light across a card face (its `.hc-glint`) — WAAPI, so restarting it never forces a layout. */
export function glint(el: HTMLElement | null | undefined) {
  const sweep = el?.querySelector<HTMLElement>('.hc-glint-sweep');
  if (!sweep || prefersReducedMotion()) return;
  sweep.animate([{ transform: 'translateX(-120%)' }, { transform: 'translateX(120%)' }], {
    duration: 1000,
    easing: 'cubic-bezier(.3,.6,.3,1)',
  });
}

/**
 * A puff of paper confetti from the card's centre, spawned into the stage and removed
 * after. `gold` = the Major Arcana moment: more pieces, all gold, drifting down longer.
 */
export function confetti(
  stage: HTMLElement | null | undefined,
  el: HTMLElement | null | undefined,
  seed = 0,
  gold = false,
  /** Rects measured earlier (while layout was clean) — reading them mid-animation forces a layout. */
  rects?: { stage: DOMRect; card: DOMRect },
) {
  if (!stage || !el || prefersReducedMotion()) return;
  const s = rects?.stage ?? stage.getBoundingClientRect();
  const r = rects?.card ?? el.getBoundingClientRect();
  const cx = r.left - s.left + r.width / 2;
  const cy = r.top - s.top + r.height * 0.45;
  const kinds = ['star', 'dot', 'strip'];
  const colors = gold ? ['#f3d58f', '#e4b45a', '#fff1c4', '#c9973f'] : ['var(--paper-stock)', '#e8c47e', '#d9868c', '#c39dd8'];
  const count = gold ? 22 : 12;
  for (let i = 0; i < count; i++) {
    const bit = document.createElement('span');
    bit.className = `hd-confetto is-${kinds[i % 3]}`;
    bit.style.left = `${cx}px`;
    bit.style.top = `${cy}px`;
    bit.style.background = colors[i % colors.length];
    stage.appendChild(bit);
    const a = (i / count) * Math.PI * 2 + (seed % 7) * 0.3;
    const dist = r.width * (0.55 + ((i * 7) % 5) * 0.12);
    const dx = Math.cos(a) * dist;
    const dy = Math.sin(a) * dist * 0.7 - r.width * 0.35;
    const spin = (i % 2 ? 1 : -1) * (180 + i * 40);
    bit
      .animate(
        [
          { transform: 'translate(-50%, -50%) scale(0.4)', opacity: 0 },
          { transform: `translate(calc(-50% + ${dx * 0.8}px), calc(-50% + ${dy}px)) rotate(${spin * 0.6}deg) scale(1)`, opacity: 1, offset: 0.35 },
          { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy + r.width * 0.9}px)) rotate(${spin}deg) scale(0.8)`, opacity: 0 },
        ],
        { duration: (gold ? 2000 : 1300) + (i % 4) * 120, easing: 'cubic-bezier(.2,.7,.4,1)' },
      )
      .finished.finally(() => bit.remove());
  }
}

/* ---------------- the entrance ---------------- */

const GLIDE_MS = 300; // in from the wing (ease-out)
const FALL_AT = 230; // the bar starts to drop as the glide settles (overlap — no dead hold)
const FALL_MS = 120; // …a short, accelerating drop
const TAUT_MS = 380; // the strings catch it: a damped recoil, settled to 0
const NEXT_AFTER = 170; // ms between landings
const LEAN_MS = 900; // the card's trail-and-swing-on, settled to 0
const LEAN_DEG = 5;

export interface EntranceCard {
  /** The card's `.hc` root (holds .pp-rig, .hc-bump, .hc-card). */
  el: HTMLElement;
  /** Its column, % of the stage. */
  x: number;
}

/**
 * The entrance, one puppet at a time from the centre out: the centre column drops from
 * the loft; the others glide in from their own wing (so none crosses a card already
 * hanging), a little high, then drop and snap taut with a small recoil. The card trails
 * a few degrees on the way in and swings on once past its mark, settling to exactly 0.
 * Cards in one column (strung under each other) come in together.
 *
 * WAAPI with `fill: backwards` on elements already at rest (call right after they
 * render). Resolves when the last puppet has landed. Reduced motion: resolves at once.
 */
export async function entrance(
  stage: HTMLElement | null | undefined,
  cards: EntranceCard[],
  onSnap?: () => void,
  /** ms before the first puppet moves (held offstage meanwhile) — e.g. after a cue. */
  startDelay = 0,
) {
  if (!stage || prefersReducedMotion() || !cards.length) return;
  const stageRect = stage.getBoundingClientRect();
  const cqw = stageRect.width / 100;
  // Columns from the centre out, alternating sides: 50, 32.5, 67.5, 15, 85…
  const columns = [...new Set(cards.map(c => c.x))].sort((a, b) => Math.abs(a - 50) - Math.abs(b - 50) || a - b);
  const high = -6 * cqw; // the bar comes in this far above its mark
  const recoil = 1.1 * cqw;
  let last = 0;

  // Scheduled by landing time, so the snaps fall on an even beat (a side puppet needs
  // its glide before it drops, the centre one doesn't).
  const firstSnap = (Math.abs(columns[0] - 50) < 1 ? 0 : FALL_AT) + FALL_MS;
  columns.forEach((colX, order) => {
    const dir = Math.abs(colX - 50) < 1 ? 0 : colX < 50 ? -1 : 1; // which wing it comes from
    const fallAt = dir ? FALL_AT : 0;
    const snapAt = fallAt + FALL_MS;
    const delay = Math.max(0, startDelay + firstSnap + order * NEXT_AFTER - snapAt);
    const duration = snapAt + TAUT_MS;
    if (onSnap) setTimeout(onSnap, delay + snapAt);
    last = Math.max(last, delay + snapAt + 60);

    for (const { el } of cards.filter(c => c.x === colX)) {
      const rig = el.querySelector<HTMLElement>('.pp-rig');
      const bump = el.querySelector<HTMLElement>('.hc-bump');
      if (!rig) continue;
      // Measured on the card (drawn below the bar) before its rig is animated, so the
      // off-stage start clears the stage edge.
      const r = (el.querySelector<HTMLElement>('.hc-card') ?? el).getBoundingClientRect();
      const offX = dir < 0 ? -(r.right - stageRect.left + 12) : stageRect.right - r.left + 12;
      const fromY = dir ? high : -(r.bottom - stageRect.top + 12);
      const xAt = (t: number) => (dir ? offX * (1 - easeOutCubic(clamp01(t / GLIDE_MS))) : 0);
      const yAt = (t: number) => {
        if (t < fallAt) return fromY;
        if (t < snapAt) return fromY * (1 - easeInQuad((t - fallAt) / FALL_MS));
        return recoil * settle(t - snapAt, TAUT_MS, 230, 0.35);
      };
      rig.animate(sampled(duration, t => `translate(${xAt(t).toFixed(1)}px, ${yAt(t).toFixed(1)}px)`), {
        duration,
        delay,
        easing: 'linear',
        fill: 'backwards',
      });
      // Trails behind the bar on the way in (leans away from the travel), swings on past
      // its mark as the bar stops, settles to 0: one designed curve, bounded to a few °.
      if (dir && bump) rock(bump, LEAN_MS, t => -dir * LEAN_DEG * settle(t, LEAN_MS, GLIDE_MS * 2, 0.28), delay);
    }
  });
  await motionSleep(last);
}

/**
 * FLIP a puppet from where it was drawn (`before`, the card's old rect) to where it is
 * now: the whole rig moves as one piece (translate + scale about the bar), easing out,
 * and the card leans a few degrees with the travel and settles. Call right after the
 * new layout is applied (same frame).
 */
export function glideTo(el: HTMLElement, before: DOMRect, duration = 700, delay = 0) {
  if (prefersReducedMotion()) return;
  const rig = el.querySelector<HTMLElement>('.pp-rig');
  const card = el.querySelector<HTMLElement>('.hc-card');
  const bump = el.querySelector<HTMLElement>('.hc-bump');
  if (!rig || !card) return;
  const after = card.getBoundingClientRect();
  const origin = rig.getBoundingClientRect(); // the rig's top centre = the bar
  const ox = origin.left + origin.width / 2;
  const oy = origin.top;
  const sc = before.width / after.width;
  // old top-centre = O + d + s·(new top-centre − O)
  const dx = before.left + before.width / 2 - ox - sc * (after.left + after.width / 2 - ox);
  const dy = before.top - oy - sc * (after.top - oy);
  const ease = (p: number) => 1 - (1 - p) ** 4; // ease-out-quart
  rig.animate(
    sampled(duration, t => {
      const k = 1 - ease(clamp01(t / duration));
      return `translate(${(dx * k).toFixed(1)}px, ${(dy * k).toFixed(1)}px) scale(${(1 + (sc - 1) * k).toFixed(4)})`;
    }),
    { duration, delay, easing: 'linear', fill: 'backwards' },
  );
  if (bump && Math.abs(dx) > 2) {
    const lean = Math.min(6, Math.abs(dx) / 45) * Math.sign(dx); // moving right (dx < 0) → trails left
    const tail = duration + 500;
    rock(bump, tail, t => lean * settle(t, tail, duration * 1.3, 0.3), delay);
  }
}

/** After the entrance: one soft gust runs across the stage, left to right. */
export function gust(cards: EntranceCard[]) {
  if (prefersReducedMotion()) return 0;
  for (const { el, x } of cards) swing(el.querySelector<HTMLElement>('.hc-bump'), 0.3, x * 4);
  return 400 + 300;
}

/** …then every card takes one small, synchronised breath: ready. */
export function breath(cards: EntranceCard[], delay = 0) {
  if (prefersReducedMotion()) return;
  for (const { el } of cards) {
    el.querySelector<HTMLElement>('.hc-card')?.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.025)', offset: 0.45 }, { transform: 'scale(1)' }],
      { duration: 650, delay, easing: 'cubic-bezier(.45,0,.3,1)', composite: 'add' },
    );
  }
}
